import type { Filter, FilterClause } from "./facetFilter";
import { isPlainObject } from "./isPlainObject";
import { valueToArray } from "./valueToArray";

export type StringMatcher = string | string[] | { all: string[] };
export type RangeMatcher = [number] | [number | null, number | null];

export type ReleaseSearchFilter = {
  format?: StringMatcher;
  formatSet?: StringMatcher;
  type?: StringMatcher;
  genre?: StringMatcher;
  style?: StringMatcher;
  country?: StringMatcher;
  artist?: { country?: StringMatcher };
  label?: StringMatcher;
  company?: StringMatcher;
  tag?: StringMatcher;
  year?: RangeMatcher;
  OR?: ReleaseSearchFilter[];
};

export type FacetedSearchState = {
  q?: string;
  sort?: string;
  page?: number;
  filter?: ReleaseSearchFilter;
};

export type ReleaseSearchFilterKey =
  | "format"
  | "formatSet"
  | "type"
  | "genre"
  | "style"
  | "country"
  | "artist.country"
  | "label"
  | "company"
  | "tag"
  | "year";

type InternalFacetKey = Exclude<ReleaseSearchFilterKey, "artist.country"> | "artistCountry";

type NormalizedStringMatcher =
  | { kind: "any"; values: string[] }
  | { kind: "all"; values: string[] };

type ConvertOptions = {
  allowed?: readonly ReleaseSearchFilterKey[];
  allTermKeys?: readonly InternalFacetKey[];
  maxOrDepth?: number;
  maxNodes?: number;
};

const DEFAULT_ALL_TERM_KEYS = ["label", "company", "tag", "formatSet"] as const;
const DEFAULT_MAX_OR_DEPTH = 3;
const DEFAULT_MAX_NODES = 64;

const STRING_FILTER_KEYS = [
  "format",
  "formatSet",
  "type",
  "genre",
  "style",
  "country",
  "label",
  "company",
  "tag",
] as const;

type ReleaseSearchStringKey = (typeof STRING_FILTER_KEYS)[number];

function uniqNonEmpty(values: readonly unknown[]): string[] {
  return [
    ...new Set(
      values.filter((value): value is string => typeof value === "string" && value.length > 0),
    ),
  ];
}

function normalizeStringMatcher(value: unknown): NormalizedStringMatcher | undefined {
  if (typeof value === "string") {
    return value ? { kind: "any", values: [value] } : undefined;
  }
  if (Array.isArray(value)) {
    const values = uniqNonEmpty(value);
    return values.length ? { kind: "any", values } : undefined;
  }
  if (isPlainObject(value) && Array.isArray(value.all)) {
    const values = uniqNonEmpty(value.all);
    return values.length ? { kind: "all", values } : undefined;
  }
  return undefined;
}

function normalizeRangeMatcher(value: unknown): RangeMatcher | undefined {
  if (!Array.isArray(value)) return undefined;
  if (value.length === 1) {
    const exact = value[0];
    return typeof exact === "number" && Number.isFinite(exact) ? [exact] : undefined;
  }
  if (value.length !== 2) return undefined;
  const [lower, upper] = value;
  const isBound = (bound: unknown): bound is number | null =>
    bound === null || (typeof bound === "number" && Number.isFinite(bound));
  return isBound(lower) && isBound(upper) ? [lower, upper] : undefined;
}

function isAllowed(
  key: ReleaseSearchFilterKey,
  allowed: ReadonlySet<ReleaseSearchFilterKey> | null,
) {
  return allowed == null || allowed.has(key);
}

function hasMatcher(value: unknown): boolean {
  return normalizeStringMatcher(value) !== undefined;
}

function orChildren(filter: ReleaseSearchFilter): ReleaseSearchFilter[] {
  return Array.isArray(filter.OR)
    ? filter.OR.filter((child): child is ReleaseSearchFilter => isPlainObject(child))
    : [];
}

function hasNonEmptyFilter(
  filter: ReleaseSearchFilter | undefined,
  options: Required<Pick<ConvertOptions, "maxOrDepth" | "maxNodes">> & {
    budget: { nodes: number };
    depth: number;
  },
): boolean {
  if (!filter) return false;
  if (options.depth > options.maxOrDepth || options.budget.nodes >= options.maxNodes) {
    return true;
  }
  options.budget.nodes += 1;

  for (const key of STRING_FILTER_KEYS) {
    if (hasMatcher(filter[key])) return true;
  }
  if (hasMatcher(filter.artist?.country)) return true;
  if (normalizeRangeMatcher(filter.year)) return true;
  return orChildren(filter).some((child) =>
    hasNonEmptyFilter(child, {
      ...options,
      depth: options.depth + 1,
    }),
  );
}

export function isReleaseSearchFilterEmpty(filter: ReleaseSearchFilter | undefined): boolean {
  return !hasNonEmptyFilter(filter, {
    maxOrDepth: DEFAULT_MAX_OR_DEPTH,
    maxNodes: DEFAULT_MAX_NODES,
    budget: { nodes: 0 },
    depth: 0,
  });
}

function matcherClause(
  key: InternalFacetKey,
  matcher: NormalizedStringMatcher,
  allTermKeys: ReadonlySet<InternalFacetKey>,
): FilterClause | undefined {
  if (!matcher.values.length) return undefined;
  if (matcher.kind === "any" || allTermKeys.has(key)) {
    return { f: key, v: matcher.values.length === 1 ? matcher.values[0]! : matcher.values };
  }
  const clauses = matcher.values.map((value): FilterClause => ({ f: key, v: value }));
  return clauses.length === 1 ? clauses[0] : { and: clauses };
}

function rangeClause(key: InternalFacetKey, range: RangeMatcher): FilterClause {
  if (range.length === 1) return { f: key, r: [range[0], range[0]] };
  return { f: key, r: [range[0], range[1]] };
}

function childFilterToClause(clauses: FilterClause[]): FilterClause | undefined {
  if (clauses.length === 0) return undefined;
  return clauses.length === 1 ? clauses[0] : { and: clauses };
}

type FilterToClausesOptions = Required<Pick<ConvertOptions, "maxOrDepth" | "maxNodes">> & {
  allowed: ReadonlySet<ReleaseSearchFilterKey> | null;
  allTermKeys: ReadonlySet<InternalFacetKey>;
  budget: { nodes: number };
  depth: number;
};

function stringFilterClauses(
  filter: ReleaseSearchFilter,
  options: FilterToClausesOptions,
): FilterClause[] {
  return STRING_FILTER_KEYS.flatMap((key) => {
    if (!isAllowed(key, options.allowed)) return [];
    const matcher = normalizeStringMatcher(filter[key]);
    if (!matcher) return [];
    const clause = matcherClause(key, matcher, options.allTermKeys);
    return clause ? [clause] : [];
  });
}

function artistCountryFilterClause(
  filter: ReleaseSearchFilter,
  options: FilterToClausesOptions,
): FilterClause[] {
  if (!isAllowed("artist.country", options.allowed)) return [];
  const matcher = normalizeStringMatcher(filter.artist?.country);
  if (!matcher) return [];
  const clause = matcherClause("artistCountry", matcher, options.allTermKeys);
  return clause ? [clause] : [];
}

function yearFilterClause(
  filter: ReleaseSearchFilter,
  options: FilterToClausesOptions,
): FilterClause[] {
  if (!isAllowed("year", options.allowed)) return [];
  const range = normalizeRangeMatcher(filter.year);
  return range ? [rangeClause("year", range)] : [];
}

function orFilterClause(
  filter: ReleaseSearchFilter,
  options: FilterToClausesOptions,
): FilterClause[] | undefined {
  const children = orChildren(filter);
  if (children.length === 0) return [];
  const converted = children.map((child) => {
    const clauses = filterToClauses(child, { ...options, depth: options.depth + 1 });
    return clauses ? childFilterToClause(clauses) : undefined;
  });
  if (converted.some((child) => child === undefined)) return undefined;
  const or = converted.filter((child): child is FilterClause => child !== undefined);
  return or.length > 0 ? [{ or }] : [];
}

function filterToClauses(
  filter: ReleaseSearchFilter,
  options: FilterToClausesOptions,
): FilterClause[] | undefined {
  if (options.depth > options.maxOrDepth || options.budget.nodes >= options.maxNodes) {
    return undefined;
  }
  options.budget.nodes += 1;
  const orClause = orFilterClause(filter, options);
  if (!orClause) return undefined;
  return [
    ...stringFilterClauses(filter, options),
    ...artistCountryFilterClause(filter, options),
    ...yearFilterClause(filter, options),
    ...orClause,
  ];
}

export function releaseSearchFilterToFilter(
  filter: ReleaseSearchFilter | undefined,
  options: ConvertOptions = {},
): Filter | undefined {
  if (isReleaseSearchFilterEmpty(filter)) return undefined;
  const allowed = options.allowed ? new Set(options.allowed) : null;
  const allTermKeys = new Set<InternalFacetKey>(options.allTermKeys ?? DEFAULT_ALL_TERM_KEYS);
  const clauses = filterToClauses(filter!, {
    allowed,
    allTermKeys,
    maxOrDepth: options.maxOrDepth ?? DEFAULT_MAX_OR_DEPTH,
    maxNodes: options.maxNodes ?? DEFAULT_MAX_NODES,
    budget: { nodes: 0 },
    depth: 0,
  });
  return clauses?.length ? clauses : undefined;
}

function matcherFromClauseValue(value: string | string[], all: boolean): StringMatcher {
  const values = [...valueToArray(value)];
  if (all && values.length > 1) return { all: values };
  if (values.length === 1) return values[0]!;
  return values;
}

function rangeMatcherFromClauseRange([lower, upper]: [number | null, number | null]): RangeMatcher {
  const isExactRange = lower != null && upper != null && lower === upper;
  if (isExactRange) return [lower];
  return [lower, upper];
}

function setMatcher(target: ReleaseSearchFilter, key: InternalFacetKey, matcher: StringMatcher) {
  if (key === "artistCountry") {
    target.artist = { ...target.artist, country: matcher };
    return;
  }
  if (key === "year") return;
  target[key as ReleaseSearchStringKey] = matcher;
}

function mergeFilter(target: ReleaseSearchFilter, source: ReleaseSearchFilter) {
  for (const key of STRING_FILTER_KEYS) {
    if (source[key] !== undefined) target[key] = source[key];
  }
  if (source.artist?.country !== undefined) {
    target.artist = { ...target.artist, country: source.artist.country };
  }
  if (source.year !== undefined) target.year = source.year;
  const children = orChildren(source);
  if (children.length) {
    const currentChildren = target.OR ?? [];
    target.OR = [...currentChildren, ...children];
  }
}

function orClauseToReleaseSearchFilter(
  clause: Extract<FilterClause, { or: FilterClause[] }>,
  allTermKeys: ReadonlySet<InternalFacetKey>,
): ReleaseSearchFilter | undefined {
  const OR = clause.or
    .map((child) => clauseToReleaseSearchFilter(child, allTermKeys))
    .filter((child): child is ReleaseSearchFilter => child !== undefined);
  return OR.length > 0 ? { OR } : undefined;
}

function andClauseToReleaseSearchFilter(
  clause: Extract<FilterClause, { and: FilterClause[] }>,
  allTermKeys: ReadonlySet<InternalFacetKey>,
): ReleaseSearchFilter | undefined {
  const target: ReleaseSearchFilter = {};
  for (const child of clause.and) {
    const converted = clauseToReleaseSearchFilter(child, allTermKeys);
    if (converted) mergeFilter(target, converted);
  }
  return isReleaseSearchFilterEmpty(target) ? undefined : target;
}

function rangeClauseToReleaseSearchFilter(
  clause: Extract<FilterClause, { r: [number | null, number | null] }>,
): ReleaseSearchFilter | undefined {
  return clause.f === "year" ? { year: rangeMatcherFromClauseRange(clause.r) } : undefined;
}

function valueClauseToReleaseSearchFilter(
  clause: Extract<FilterClause, { v: string | string[] }>,
  allTermKeys: ReadonlySet<InternalFacetKey>,
): ReleaseSearchFilter | undefined {
  const fields = valueToArray(clause.f);
  if (fields.length > 1) {
    return {
      OR: fields.map((field) => {
        const child: ReleaseSearchFilter = {};
        const key = field as InternalFacetKey;
        setMatcher(child, key, matcherFromClauseValue(clause.v, allTermKeys.has(key)));
        return child;
      }),
    };
  }
  const key = fields[0] as InternalFacetKey | undefined;
  if (!key) return undefined;
  const target: ReleaseSearchFilter = {};
  setMatcher(target, key, matcherFromClauseValue(clause.v, allTermKeys.has(key)));
  return target;
}

function clauseToReleaseSearchFilter(
  clause: FilterClause,
  allTermKeys: ReadonlySet<InternalFacetKey>,
): ReleaseSearchFilter | undefined {
  if ("or" in clause) return orClauseToReleaseSearchFilter(clause, allTermKeys);
  if ("and" in clause) return andClauseToReleaseSearchFilter(clause, allTermKeys);
  if ("r" in clause) return rangeClauseToReleaseSearchFilter(clause);
  return valueClauseToReleaseSearchFilter(clause, allTermKeys);
}

export function filterToReleaseSearchFilter(
  filter: Filter | undefined,
  options: Pick<ConvertOptions, "allTermKeys"> = {},
): ReleaseSearchFilter | undefined {
  if (!filter?.length) return undefined;
  const allTermKeys = new Set<InternalFacetKey>(options.allTermKeys ?? DEFAULT_ALL_TERM_KEYS);
  const target: ReleaseSearchFilter = {};
  for (const clause of filter) {
    const converted = clauseToReleaseSearchFilter(clause, allTermKeys);
    if (converted) mergeFilter(target, converted);
  }
  return isReleaseSearchFilterEmpty(target) ? undefined : target;
}
